"use client";

import clsx from "clsx";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "@/components/library/Icon";
import { BETA_LANGUAGES, SupportedLanguages, getFlag } from "@/enums/internationalization";
import { useOrg } from "@/hooks/useOrg";
import { OnboardingStepHeader } from "./OnboardingStepHeader";
import { StepFooter } from "./StepFooter";

interface LanguageSelectionStepProps {
  readonly onBack?: () => void;
  readonly onNext: () => void;
}

export function LanguageSelectionStep({ onBack, onNext }: LanguageSelectionStepProps) {
  const { t } = useTranslation("onboarding");
  const { t: ct } = useTranslation("common");
  const { agentProfile, saveAgentProfile } = useOrg();

  const [selectedLanguages, setSelectedLanguages] = useState<SupportedLanguages[]>(() => {
    const existing = agentProfile?.languages ?? [];
    return existing.length > 0 ? existing : [SupportedLanguages.English];
  });
  const [isSaving, setIsSaving] = useState(false);

  const toggleLanguage = useCallback((lang: SupportedLanguages) => {
    setSelectedLanguages((prev) => {
      if (prev.includes(lang)) {
        // Don't allow deselecting the last language
        if (prev.length <= 1) return prev;
        return prev.filter((l) => l !== lang);
      }
      return [...prev, lang];
    });
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await saveAgentProfile({ languages: selectedLanguages });
      onNext();
    } catch (error) {
      console.error("Failed to save languages", error);
    } finally {
      setIsSaving(false);
    }
  }, [selectedLanguages, saveAgentProfile, onNext]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-8 flex flex-col items-center justify-center">
        <div className="w-full max-w-lg space-y-8">
          <OnboardingStepHeader
            title={t("languageSelection.title")}
            description={t("languageSelection.subtitle")}
          />

          {/* Icon */}
          <div className="flex justify-center">
            <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Icon icon="globe" className="w-10 h-10 text-primary" />
            </div>
          </div>

          {/* Language grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {Object.values(SupportedLanguages).map((lang) => {
              const isSelected = selectedLanguages.includes(lang);
              const isBeta = BETA_LANGUAGES.includes(lang);

              return (
                <button
                  key={lang}
                  type="button"
                  onClick={() => toggleLanguage(lang)}
                  className={clsx(
                    "relative flex items-center gap-2 px-3 py-3 rounded-xl border-2 transition-all duration-200 overflow-hidden",
                    "text-left text-sm min-w-0",
                    isSelected
                      ? "border-primary bg-primary/5"
                      : "border-200 bg-panel hover:border-300"
                  )}
                >
                  {isBeta && (
                    <span className="absolute top-0.5 right-1.5 text-[9px] text-muted-foreground font-medium uppercase tracking-wider">
                      {ct("beta")}
                    </span>
                  )}
                  <span className="text-lg shrink-0">{getFlag(lang)}</span>
                  <span
                    className={clsx(
                      "font-medium truncate",
                      isSelected ? "text-foreground" : "text-700"
                    )}
                  >
                    {ct(lang)}
                  </span>
                  {/* Check indicator */}
                  {isSelected && (
                    <div className="ml-auto w-4 h-4 rounded-full bg-primary flex items-center justify-center shrink-0">
                      <svg
                        className="w-2.5 h-2.5 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={3}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          <p className="text-sm text-center text-muted-foreground">{t("languageSelection.hint")}</p>
        </div>
      </div>

      {/* Footer Buttons */}
      <StepFooter onBack={onBack} onSkip={onNext} onNext={handleSave} isSaving={isSaving} />
    </div>
  );
}
